import React from 'react';
import { Box } from 'roo-ui/components';
import { ImageFallback } from '../ImageFallback';
import { ImageLoadingIndicator } from '../ImageLoadingIndicator';
import { ImageInner } from '../ImageInner';
import { useImageLoader } from './useImageLoader';

export interface ImageLoaderProps {
  src: string;
  alt: string;
  srcSet?: string;
  onLoad?: Function;
  width?: string | number | string[] | number[];
  height?: string | number | string[] | number[];
  cover?: boolean;
  [key: string]: any;
}

export const ImageLoader = ({
  src,
  alt,
  srcSet,
  onLoad,
  width,
  height,
  cover = true,
  ...rest
}: ImageLoaderProps) => {
  const { isLoading, displayedLoading, error, loadedImg } = useImageLoader({
    src,
    alt,
    srcSet,
    onLoad,
  });

  const isImageReady = loadedImg && (!!loadedImg.src || !!loadedImg.srcSet);
  const hasSuppliedDimensions = !!width || !!height;

  // This keeps a placeholder width/height should an image take less than 500ms to show.
  const showPlaceholder = !error && !isLoading && !isImageReady;

  if (showPlaceholder) {
    return <Box width={width} height={height} data-testid="placeholder" />;
  }

  if (isLoading) {
    return <ImageLoadingIndicator {...rest} width={width} height={height} />;
  }

  if (error) {
    return <ImageFallback {...rest} width={width} height={height} />;
  }

  if (loadedImg && isImageReady) {
    return (
      <ImageInner
        {...rest}
        src={loadedImg.src}
        alt={loadedImg.alt}
        srcSet={loadedImg.srcSet}
        width={hasSuppliedDimensions ? width || '100%' : loadedImg.naturalWidth}
        height={
          hasSuppliedDimensions ? height || '100%' : loadedImg.naturalHeight
        }
        animate={displayedLoading}
        cover={cover}
      />
    );
  }
  return null;
};
